import { Head, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import FormTextarea from '@/Components/FormTextarea';
import PageHeader from '@/Components/PageHeader';
import { ArrowLeftIcon, PlusIcon, TrashIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

export default function Create() {
    const { data, setData, post, processing, errors } = useForm({
        name: '',
        description: '',
        departments: [],
    });

    const addDepartment = () => {
        setData('departments', [...data.departments, { name: '' }]);
    };

    const updateDepartment = (index, value) => {
        const updated = [...data.departments];
        updated[index] = { ...updated[index], name: value };
        setData('departments', updated);
    };

    const removeDepartment = (index) => {
        setData('departments', data.departments.filter((_, i) => i !== index));
    };

    const submit = (e) => {
        e.preventDefault();
        post('/work-locations');
    };

    return (
        <AppLayout>
            <Head title="Add Location" />
            <PageHeader
                title="Add Location"
                subtitle="Work Locations › Add Location"
                actions={
                    <Button href="/work-locations" variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            <form onSubmit={submit}>
                <Card>
                    <CardBody className="space-y-4 max-w-lg">
                        <FormInput
                            label="Name"
                            value={data.name}
                            onChange={e => setData('name', e.target.value)}
                            error={errors.name}
                            placeholder="Enter location name"
                            required
                        />
                        <FormTextarea
                            label="Description"
                            value={data.description}
                            onChange={e => setData('description', e.target.value)}
                            error={errors.description}
                            rows={3}
                            placeholder="Optional description of this work location"
                        />
                    </CardBody>
                </Card>

                {/* Departments Section */}
                <Card className="mt-6">
                    <CardBody>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <BuildingOfficeIcon className="w-5 h-5 text-emerald-700" />
                                <h3 className="text-sm font-semibold text-slate-800">Departments</h3>
                                <span className="text-xs text-slate-400">({data.departments.length})</span>
                            </div>
                            <button
                                type="button"
                                onClick={addDepartment}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition"
                            >
                                <PlusIcon className="w-3.5 h-3.5" /> Add Department
                            </button>
                        </div>

                        {data.departments.length === 0 ? (
                            <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
                                <BuildingOfficeIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                <p className="text-sm text-slate-400 mb-3">No departments added yet</p>
                                <button
                                    type="button"
                                    onClick={addDepartment}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition"
                                >
                                    <PlusIcon className="w-3.5 h-3.5" /> Add First Department
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {data.departments.map((dept, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <span className="text-xs text-slate-400 w-6 text-right">{index + 1}.</span>
                                        <input
                                            type="text"
                                            value={dept.name}
                                            onChange={e => updateDepartment(index, e.target.value)}
                                            placeholder="Department name"
                                            className="flex-1 rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20 transition px-4 py-2.5"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeDepartment(index)}
                                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                            title="Remove department"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {errors['departments'] && (
                                    <p className="text-xs text-red-500 mt-1">{errors['departments']}</p>
                                )}
                            </div>
                        )}
                    </CardBody>
                </Card>

                <div className="flex gap-3 mt-6">
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Creating...' : 'Create Location'}
                    </Button>
                    <Button href="/work-locations" variant="outline">Cancel</Button>
                </div>
            </form>
        </AppLayout>
    );
}
